import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import OpportunityStatusChip from './OpportunityStatusChip';
import RejectOpportunityDialog from '../../CourseDetails/Pages/Evaluations/Components/EvaluationCard/Components/RejectOpportunityDialog';
import useAdminOpportunityActions from '../../CourseDetails/Pages/Evaluations/Components/EvaluationCard/Hooks/useAdminOpportunityActions';
import type { OpportunityRequestListItemI } from 'types';
import type { OpportunityRequestPatchedI } from 'Api/axios/evaluation-opportunity-requests-api';
import { useHasPermission, EVALUATION_OPPORTUNITY_PERMISSIONS } from 'Utils/permissions';

const EMPTY_MESSAGE = 'No hay solicitudes para mostrar';

function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

interface RowActionsProps {
  row: OpportunityRequestListItemI;
  onActionSuccess: (patched: OpportunityRequestPatchedI, rowId: string) => void;
}

function RowActions({ row, onActionSuccess }: RowActionsProps) {
  const canReview = useHasPermission(EVALUATION_OPPORTUNITY_PERMISSIONS.REVIEW);
  const [rejectOpen, setRejectOpen] = useState(false);

  const { approve, reject } = useAdminOpportunityActions(row.id, (patched) => {
    onActionSuccess(patched, row.id);
  });

  if (!canReview || row.status !== 'pending') return null;

  return (
    <>
      <Button variant="contained" color="success" size="small" onClick={approve} sx={{ mr: 1 }}>
        Aprobar
      </Button>
      <Button variant="contained" color="error" size="small" onClick={() => setRejectOpen(true)}>
        Rechazar
      </Button>
      <RejectOpportunityDialog
        open={rejectOpen}
        onReject={(comment) => { setRejectOpen(false); reject(comment); }}
        onCancel={() => setRejectOpen(false)}
      />
    </>
  );
}

interface Props {
  rows: OpportunityRequestListItemI[];
  onActionSuccess: (patched: OpportunityRequestPatchedI, rowId: string) => void;
}

/**
 * MUI table for the opportunity requests tab.
 * Columns: Empleado, Nómina, Evaluación, Estado, Fecha, Acciones
 */
export default function OpportunityRequestsTable({ rows, onActionSuccess }: Props) {
  if (rows.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography color="text.secondary">{EMPTY_MESSAGE}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Empleado</TableCell>
            <TableCell>Nómina</TableCell>
            <TableCell>Evaluación</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Fecha</TableCell>
            <TableCell>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.employee.fullName}</TableCell>
              <TableCell>{row.employee.payrollNum}</TableCell>
              <TableCell>{row.lesson.name}</TableCell>
              <TableCell>
                <OpportunityStatusChip status={row.status} />
              </TableCell>
              <TableCell>{formatDate(row.createdAt)}</TableCell>
              <TableCell>
                <RowActions row={row} onActionSuccess={onActionSuccess} />
                {row.comment && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {row.comment}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
